import {
    SpacingToken,
    UiStack,
    UiText,
} from '@experiences/ui-common';
import { FontVariantToken } from '@uipath/apollo-core';
import {
    ApAlertBar,
    ApDropdown,
    ApDropdownItem,
    ApIcon,
} from '@uipath/portal-shell-react';
import React from 'react';
import { useIntl } from 'react-intl';

import {
    WizardStepContainer,
    WizardStepHeader,
} from '../WizardStepLayout';
import useGovernancePolicyCreateScopeStepViewModel from './GovernancePolicyCreateScopeStepViewModel';

interface GovernancePolicyCreateScopeStepProps {
    type?: 'add' | 'edit';
}

export const GovernancePolicyCreateScopeStep: React.FC<GovernancePolicyCreateScopeStepProps> = ({ type = 'add' }) => {
    const { formatMessage: translate } = useIntl();
    const {
        tenantId,
        hasPolicyData,
        tenants,
        isLoadingTenants,
        handleTenantChange,
        isTenantScoped,
        dropdownKey,
    } = useGovernancePolicyCreateScopeStepViewModel();

    return (
        <WizardStepContainer type={type}>
            <UiStack
                direction="column"
                justify="between"
                h="inherit"
                data-cy="governance-policy-create-scope-step"
            >
                <UiStack
                    direction="column"
                    gap={SpacingToken.L}
                >
                    {type === 'add' && (
                        <UiStack
                            direction="column"
                            gap={SpacingToken.S}
                            mb={SpacingToken.M}
                            data-cy="governance-create-policy-intro"
                        >
                            <UiText
                                variant={FontVariantToken.fontSizeH3Bold}
                                data-cy="governance-create-policy-intro-title"
                            >
                                {translate({ id: 'CLIENT_GOVERNANCE_CREATE_POLICY_INTRO_TITLE' })}
                            </UiText>
                            <UiText
                                variant={FontVariantToken.fontSizeM}
                                color="var(--color-foreground-de-emp)"
                                data-cy="governance-create-policy-intro-description"
                            >
                                {translate({ id: 'CLIENT_GOVERNANCE_CREATE_POLICY_INTRO_DESCRIPTION' })}
                            </UiText>
                        </UiStack>
                    )}
                    <WizardStepHeader
                        title={translate({ id: 'CLIENT_GOVERNANCE_STEP_SCOPE_TITLE' })}
                        description={translate({ id: 'CLIENT_GOVERNANCE_STEP_SCOPE_DESCRIPTION' })}
                        titleVariant={FontVariantToken.fontSizeH4Bold}
                    />
                    <UiStack
                        direction="column"
                        gap={SpacingToken.S}
                    >
                        <ApDropdown
                            key={dropdownKey}
                            style={{ width: '400px' }}
                            label={translate({ id: 'CLIENT_GOVERNANCE_SCOPE_TENANT_LABEL' })}
                            placeholder={translate({ id: 'CLIENT_GOVERNANCE_SCOPE_TENANT_PLACEHOLDER' })}
                            selectedValue={tenantId}
                            onSelectedValueChanged={handleTenantChange}
                            required
                            data-cy="governance-scope-tenant-dropdown"
                            disabled={isLoadingTenants || type === 'edit' || isTenantScoped}
                        >
                            {tenants?.map((tenant: any) => (
                                <ApDropdownItem
                                    value={tenant.id}
                                    label={tenant.name}
                                    key={`tenant-dropdown-item-${tenant.id}`}
                                    data-cy={`tenant-dropdown-item-${tenant.id}`}
                                />
                            ))}
                        </ApDropdown>
                    </UiStack>
                    {isTenantScoped && (
                        <ApAlertBar
                            status="info"
                            cancelable={false}
                            data-cy="governance-policy-create-scope-readonly-alert"
                        >
                            {translate({ id: 'CLIENT_GOVERNANCE_SCOPE_TENANT_READONLY_MESSAGE' })}
                        </ApAlertBar>
                    )}
                    <UiStack
                        direction="row"
                        gap={SpacingToken.S}
                        align="center"
                        data-cy="governance-policy-create-scope-info"
                    >
                        <ApIcon
                            color="var(--color-info-icon)"
                            name="info"
                            size='16px' />
                        <UiText>
                            {translate({ id: 'CLIENT_GOVERNANCE_SCOPE_INFO_MESSAGE' })}
                        </UiText>
                    </UiStack>
                </UiStack>
                {(hasPolicyData && type === 'add' && !!tenantId) && (
                    <UiStack pb="24px">
                        <ApAlertBar
                            status="warning"
                            cancelable={false}
                            data-cy="governance-policy-create-scope-warning"
                        >
                            {translate({ id: 'CLIENT_GOVERNANCE_SCOPE_TENANT_WARNING_MESSAGE' })}
                        </ApAlertBar>
                    </UiStack>
                )}
            </UiStack>
        </WizardStepContainer>
    );
};
